/**
 * CustomerPortal — public-facing branded portal for end-clients.
 *
 * Routes:
 *   /portal/:slug                     → landing (email entry → magic link)
 *   /portal/:slug/verify?token=...    → verify token, redirect to dashboard
 *   /portal/:slug/dashboard           → authenticated portal (jobs + messages)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import {
  CircleNotch,
  EnvelopeSimple,
  CheckCircle,
  ArrowRight,
  PaperPlaneTilt,
  ChatCircleText,
  ListChecks,
  SignOut,
  WarningCircle,
} from '@phosphor-icons/react';

const fetchPortal = (path, opts = {}) => fetch(`${BACKEND_URL}/api/service-os/portal${path}`, {
  credentials: 'include',
  ...opts,
});

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

/* ───── Branded shell ───── */
const PortalShell = ({ business, children, onSignOut }) => (
  <div className="min-h-screen bg-[#0B0F14] text-[#F0F4F8]" data-testid="customer-portal-shell">
    <header className="border-b border-[rgba(255,255,255,0.06)] bg-[#0E1420]">
      <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {business?.logo_url ? (
            <img src={business.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ background: `${business?.primary_color || '#00E5C0'}22`, color: business?.primary_color || '#00E5C0' }}
            >
              {(business?.business_name || 'P').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-xs text-[#7D8896]">Customer Portal</p>
            <h1 className="text-sm font-semibold text-[#F0F4F8]" data-testid="portal-business-name">
              {business?.business_name || 'Loading…'}
            </h1>
          </div>
        </div>
        {onSignOut && (
          <button
            onClick={onSignOut}
            data-testid="portal-signout-btn"
            className="flex items-center gap-1.5 text-xs text-[#7D8896] hover:text-[#F0F4F8] transition-colors"
          >
            <SignOut size={14} />
            Sign out
          </button>
        )}
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-8">{children}</main>
  </div>
);

/* ───── Landing: email entry ───── */
export const PortalLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [bizError, setBizError] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetchPortal(`/public/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setBiz)
      .catch(() => setBizError(true));
  }, [slug]);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetchPortal(`/${slug}/auth/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setSent(true);
        // Convenience: in dev environments the API exposes the magic URL for testing
        const data = await res.json().catch(() => ({}));
        if (data.dev_magic_url) {
          // eslint-disable-next-line no-console
          console.info('[portal dev] magic link:', data.dev_magic_url);
        }
      } else if (res.status === 429) {
        toast.error('Too many requests. Wait a minute and try again.');
      } else {
        // Always behave like sent on the UI to avoid leaking
        setSent(true);
      }
    } catch {
      toast.error('Could not send link. Try again.');
    }
    setSubmitting(false);
  };

  if (bizError) {
    return (
      <PortalShell business={null}>
        <div className="text-center py-16" data-testid="portal-not-found">
          <WarningCircle size={40} className="text-[#F59E0B] mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-1">Portal not found</h2>
          <p className="text-sm text-[#B0B8C4]">This portal link is no longer active.</p>
        </div>
      </PortalShell>
    );
  }

  if (sent) {
    return (
      <PortalShell business={biz}>
        <div className="text-center py-12" data-testid="portal-link-sent">
          <CheckCircle size={48} className="mx-auto mb-3" style={{ color: biz?.primary_color || '#00E5C0' }} weight="duotone" />
          <h2 className="text-xl font-semibold mb-2">Check your email</h2>
          <p className="text-sm text-[#B0B8C4] max-w-sm mx-auto">
            If <span className="text-[#F0F4F8]">{email}</span> matches a customer record, we've sent a secure
            sign-in link. It expires in 30 minutes.
          </p>
          <button
            onClick={() => { setSent(false); setEmail(''); }}
            className="mt-6 text-xs text-[#7D8896] hover:text-[#F0F4F8] transition-colors"
            data-testid="portal-try-different-email"
          >
            Use a different email
          </button>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell business={biz}>
      <div className="max-w-md mx-auto pt-12" data-testid="portal-landing">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold mb-2">Welcome back</h2>
          <p className="text-sm text-[#B0B8C4]">
            Sign in with your email to view your jobs and message {biz?.business_name || 'us'}.
          </p>
        </div>
        <form onSubmit={handleRequest} className="space-y-3">
          <div className="relative">
            <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8896]" />
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="portal-email-input"
              className="w-full pl-9 pr-3 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[#00E5C0]"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            data-testid="portal-request-link-btn"
            className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            style={{
              background: biz?.primary_color || '#00E5C0',
              color: '#0B0F14',
            }}
          >
            {submitting ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />}
            Send sign-in link
          </button>
        </form>
        <p className="text-[10px] text-[#5C6778] text-center mt-6">
          Secured by magic-link auth. No password needed.
        </p>
      </div>
    </PortalShell>
  );
};

/* ───── Verify: handle ?token= and redirect ───── */
export const PortalVerify = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Missing token. Request a new sign-in link.');
      return;
    }
    fetchPortal(`/${slug}/auth/verify?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || 'Invalid link')))
      .then(() => navigate(`/portal/${slug}/dashboard`, { replace: true }))
      .catch((msg) => setError(typeof msg === 'string' ? msg : 'Sign-in failed.'));
  }, [token, slug, navigate]);

  return (
    <PortalShell business={null}>
      <div className="text-center py-16" data-testid="portal-verify">
        {error ? (
          <>
            <WarningCircle size={40} className="text-[#F59E0B] mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-2">{error}</h2>
            <button
              onClick={() => navigate(`/portal/${slug}`, { replace: true })}
              className="text-xs text-[#00E5C0] hover:underline"
              data-testid="portal-verify-restart"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <CircleNotch size={32} className="animate-spin text-[#00E5C0] mx-auto mb-3" />
            <p className="text-sm text-[#B0B8C4]">Signing you in…</p>
          </>
        )}
      </div>
    </PortalShell>
  );
};

/* ───── Dashboard: jobs + message thread ───── */
export const PortalDashboard = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs');

  const loadAll = useCallback(async () => {
    try {
      const [meRes, jobsRes, msgsRes] = await Promise.all([
        fetchPortal('/me'),
        fetchPortal('/my-jobs'),
        fetchPortal('/messages'),
      ]);
      if (!meRes.ok) {
        setAuthError(true);
        setLoading(false);
        return;
      }
      setMe(await meRes.json());
      setJobs(jobsRes.ok ? (await jobsRes.json()).jobs : []);
      setMessages(msgsRes.ok ? (await msgsRes.json()).messages : []);
    } catch {
      setAuthError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSend = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const res = await fetchPortal('/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: draft.trim() }),
      });
      if (res.ok) {
        setDraft('');
        toast.success('Message sent.');
        loadAll();
      } else {
        toast.error('Could not send message.');
      }
    } catch { toast.error('Could not send message.'); }
    setSending(false);
  };

  const handleSignOut = async () => {
    try {
      await fetchPortal('/auth/logout', { method: 'POST' });
    } catch {}
    navigate(`/portal/${slug}`, { replace: true });
  };

  if (loading) {
    return (
      <PortalShell business={null}>
        <div className="flex items-center justify-center py-16">
          <CircleNotch size={24} className="animate-spin text-[#00E5C0]" />
        </div>
      </PortalShell>
    );
  }

  if (authError || !me) {
    return (
      <PortalShell business={null}>
        <div className="text-center py-16" data-testid="portal-dashboard-auth-error">
          <WarningCircle size={40} className="text-[#F59E0B] mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2">Sign in required</h2>
          <button
            onClick={() => navigate(`/portal/${slug}`, { replace: true })}
            className="text-xs text-[#00E5C0] hover:underline"
            data-testid="portal-go-signin"
          >
            Go to sign-in
          </button>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell business={me} onSignOut={handleSignOut}>
      <div data-testid="portal-dashboard">
        <div className="mb-6">
          <p className="text-xs text-[#7D8896]">Signed in as</p>
          <p className="text-sm text-[#F0F4F8]" data-testid="portal-me-email">{me.email}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[rgba(255,255,255,0.08)]">
          <button
            onClick={() => setActiveTab('jobs')}
            data-testid="portal-tab-jobs"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'jobs' ? 'text-[#F0F4F8] border-b-2 -mb-px' : 'text-[#B0B8C4] hover:text-[#F0F4F8]'
            }`}
            style={activeTab === 'jobs' ? { borderColor: me.primary_color || '#00E5C0' } : {}}
          >
            <ListChecks size={16} />
            My Jobs
            {jobs.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(255,255,255,0.06)]">
                {jobs.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            data-testid="portal-tab-messages"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'messages' ? 'text-[#F0F4F8] border-b-2 -mb-px' : 'text-[#B0B8C4] hover:text-[#F0F4F8]'
            }`}
            style={activeTab === 'messages' ? { borderColor: me.primary_color || '#00E5C0' } : {}}
          >
            <ChatCircleText size={16} />
            Messages
          </button>
        </div>

        {activeTab === 'jobs' && (
          <div data-testid="portal-jobs-tab">
            {jobs.length === 0 ? (
              <div className="text-center py-12 bg-[#1F2937] border border-[rgba(255,255,255,0.06)] rounded-xl">
                <ListChecks size={32} className="text-[#7D8896] mx-auto mb-2" />
                <p className="text-sm text-[#7D8896]">No jobs on file yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    data-testid={`portal-job-${job.id}`}
                    className="bg-[#1F2937] border border-[rgba(255,255,255,0.06)] rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <p className="text-sm font-medium text-[#F0F4F8] truncate">{job.subject}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(255,255,255,0.06)] text-[#B0B8C4] capitalize shrink-0">
                        {job.status}
                      </span>
                    </div>
                    {job.summary && <p className="text-xs text-[#B0B8C4] line-clamp-2 mb-2">{job.summary}</p>}
                    <div className="flex items-center gap-2 text-[10px] text-[#5C6778]">
                      <span className="capitalize">{job.kind}</span>
                      <span>·</span>
                      <span>{formatDate(job.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'messages' && (
          <div data-testid="portal-messages-tab">
            <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.06)] rounded-xl p-4 mb-4">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 4000))}
                placeholder="Send a message to the team…"
                rows={4}
                data-testid="portal-message-input"
                className="w-full bg-transparent text-sm text-[#F0F4F8] placeholder-[#5C6778] resize-none focus:outline-none"
              />
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-[rgba(255,255,255,0.06)]">
                <span className="text-[10px] text-[#5C6778]">{draft.length} / 4000</span>
                <button
                  onClick={handleSend}
                  disabled={sending || !draft.trim()}
                  data-testid="portal-message-send-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                  style={{ background: me.primary_color || '#00E5C0', color: '#0B0F14' }}
                >
                  {sending ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="bold" />}
                  Send
                </button>
              </div>
            </div>

            {messages.length === 0 ? (
              <div className="text-center py-8 text-xs text-[#7D8896]">No messages yet.</div>
            ) : (
              <div className="space-y-2" data-testid="portal-messages-list">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`portal-message-${m.id}`}
                    className="bg-[#1F2937] border border-[rgba(255,255,255,0.06)] rounded-xl p-3"
                  >
                    <p className="text-xs text-[#F0F4F8] whitespace-pre-wrap">{m.body_text}</p>
                    <p className="text-[10px] text-[#5C6778] mt-2">{formatDate(m.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </PortalShell>
  );
};

export default PortalLanding;
